import {normalizeError} from './errors';
import {debugLogApiError} from './utils/debugLogApiError';
import type {
  ApiMethodConfig,
  ApiOptions,
  CreateApiParams,
  DefineApiSliceParams,
  MethodInvoker,
  MethodInvokerMap,
  MethodShape,
} from './types';
import {buildUrl} from './utils/buildUrl';

export class ApiSegment {
  url: string;
  methods: Record<string, MethodInvoker<unknown, unknown>> = {};
  options: ApiOptions;

  constructor(
    domain: string,
    path: string,
    options: ApiOptions
  ) {
    this.url = buildUrl(domain, path);
    this.options = options;
  }

  addMethod<TParams, TResult, TRaw = TResult>(
    name: string,
    config: ApiMethodConfig<TParams, TResult, TRaw> = {}
  ): MethodInvoker<TParams, TResult> {
    const transport = config.transport ?? this.options.defaultTransport;

    if (!transport) {
      throw new Error('Api transport is not configured!');
    }

    const invoke: MethodInvoker<TParams, TResult> = async (params: TParams, requestOptions) => {
      try {
        const raw = await transport(this.url, name, params, requestOptions);
        return (config.mapResponse ? config.mapResponse(raw as TRaw) : raw) as TResult;
      } catch (error) {
        const normalized = normalizeError(error);
        debugLogApiError(normalized, {methodName: name});
        throw normalized;
      }
    };

    this.methods[name] = invoke as MethodInvoker<unknown, unknown>;
    (this as Record<string, unknown>)[name] = invoke;

    return invoke;
  }

  getUrl() {
    return this.url;
  }
}

export const createApi = ({domain, path, options}: CreateApiParams) => new ApiSegment(domain, path, options);

export const defineApiSlice = <TMethods extends Record<string, MethodShape>>({
  methods,
  domain,
  path,
  defaultTransport,
}: DefineApiSliceParams<TMethods>) => {
  const api = createApi({
    domain,
    path,
    options: {defaultTransport},
  });

  (Object.keys(methods) as (keyof TMethods)[]).forEach((name) => {
    const config = (methods)[name] ?? {};
    api.addMethod<TMethods[typeof name]['params'], TMethods[typeof name]['result']>(String(name), config);
  });

  return api as typeof api & MethodInvokerMap<TMethods>;
};
